import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { localDateISO, todayISO } from "@/lib/format";
import type { RangeFilter } from "@/lib/dashApi";

export type PresetKey =
  | "today"
  | "yesterday"
  | "this_week"
  | "this_month"
  | "last_month"
  | "7d"
  | "30d"
  | "90d"
  | "custom";

export const PRESET_LABELS: { key: PresetKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
];

// Format the LOCAL calendar date. toISOString() renders the UTC date, which
// is yesterday during the first hour(s) of the day anywhere ahead of UTC.
const iso = localDateISO;

/** Resolve a preset to a {from, to} inclusive date range in local time. */
export function presetRange(key: PresetKey): { from: string; to: string } {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = iso(startOfToday);
  switch (key) {
    case "today":
      return { from: to, to };
    case "yesterday": {
      const y = new Date(startOfToday);
      y.setDate(y.getDate() - 1);
      return { from: iso(y), to: iso(y) };
    }
    case "this_week": {
      const d = new Date(startOfToday);
      const dow = (d.getDay() + 6) % 7; // Monday = 0
      d.setDate(d.getDate() - dow);
      return { from: iso(d), to };
    }
    case "this_month":
      return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to };
    case "last_month": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: iso(first), to: iso(last) };
    }
    case "7d":
    case "30d":
    case "90d": {
      const days = key === "7d" ? 7 : key === "30d" ? 30 : 90;
      const d = new Date(startOfToday);
      // Inclusive range: today counts as day 1, so step back days-1.
      d.setDate(d.getDate() - (days - 1));
      return { from: iso(d), to };
    }
    default:
      return { from: to, to };
  }
}

type FiltersState = {
  preset: PresetKey;
  from: string;
  to: string;
  branch: string;
  isLive: boolean;
  range: RangeFilter;
  setPreset: (p: PresetKey) => void;
  setCustom: (from: string, to: string) => void;
  setBranch: (b: string) => void;
};

const FiltersContext = createContext<FiltersState | null>(null);

export function FiltersProvider({ children }: { children: ReactNode }) {
  const [preset, setPresetState] = useState<PresetKey>("30d");
  const init = presetRange("30d");
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [branch, setBranch] = useState("");

  function setPreset(p: PresetKey) {
    setPresetState(p);
    if (p !== "custom") {
      const r = presetRange(p);
      setFrom(r.from);
      setTo(r.to);
    }
  }
  function setCustom(f: string, t: string) {
    setPresetState("custom");
    setFrom(f);
    setTo(t);
  }

  const value = useMemo<FiltersState>(() => {
    // Snapshot mode (fast) is used only for the exact default 30-day window
    // with no branch; any other selection queries sources live for accuracy.
    const isDefault = preset === "30d" && branch === "" && to === todayISO();
    const isLive = !isDefault;
    const range: RangeFilter = isLive
      ? { from, to, branch: branch || undefined, live: true }
      : {};
    return { preset, from, to, branch, isLive, range, setPreset, setCustom, setBranch };
  }, [preset, from, to, branch]);

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters(): FiltersState {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error("useFilters must be used within <FiltersProvider>");
  return ctx;
}
